import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, TextField, Button, MenuItem, Select, FormControl,
  InputLabel, CircularProgress, Alert, Paper, Stack, Dialog, DialogTitle,
  DialogContent, DialogActions, ToggleButton, ToggleButtonGroup, Autocomplete,
  Checkbox, Chip
} from '@mui/material';
import { useAuth } from '../auth';
import { baseBackendUrl } from '../service/baseService';

interface SmsRecipient {
  id: number;
  fullName: string;
  phoneNumber: string;
  jhiGroup: string;
}

// Rough GSM-7 charset check so we can warn admins before they accidentally send a
// multi-segment (multi-charge) text. Not exhaustive, but covers normal usage.
const GSM7_REGEX = /^[A-Za-z0-9 \r\n@£$¥èéùìòÇØøÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ!"#¤%&'()*+,\-./:;<=>?¡ÄÖÑÜ§¿äöñüà^{}\\[~\]|€]*$/;

function computeSmsSegments(message: string) {
  if (!message) {
    return { encoding: 'GSM-7', segments: 0, length: 0 };
  }
  const isGsm7 = GSM7_REGEX.test(message);
  const singleLimit = isGsm7 ? 160 : 70;
  const multiLimit = isGsm7 ? 153 : 67;
  const length = message.length;
  const segments = length <= singleLimit ? 1 : Math.ceil(length / multiLimit);
  return { encoding: isGsm7 ? 'GSM-7' : 'Unicode', segments, length };
}

const SendSms: React.FC = () => {
  const { IsAdmin, cookies } = useContext(useAuth);
  const [recipients, setRecipients] = useState<SmsRecipient[]>([]);
  const [loadingRecipients, setLoadingRecipients] = useState<boolean>(true);
  const [mode, setMode] = useState<'group' | 'individual'>('group');
  const [selectedGroup, setSelectedGroup] = useState<string>('');
  const [selectedIndividuals, setSelectedIndividuals] = useState<SmsRecipient[]>([]);
  const [message, setMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);

  useEffect(() => {
    const fetchRecipients = async () => {
      if (!cookies?.['UserAuth']?.token) {
        setError('Authentication token not found');
        setLoadingRecipients(false);
        return;
      }
      try {
        const response = await fetch(`${baseBackendUrl}/api/sms/recipients`, {
          headers: { 'Authorization': `Bearer ${cookies['UserAuth'].token}` }
        });
        if (!response.ok) throw new Error('Failed to fetch recipients');
        const data: SmsRecipient[] = await response.json();
        setRecipients(data);
      } catch (err) {
        setError('Failed to load recipients');
      } finally {
        setLoadingRecipients(false);
      }
    };

    fetchRecipients();
  }, [cookies]);

  const groups = useMemo(() => {
    const counts = new Map<string, number>();
    recipients.forEach((r) => {
      if (!r.jhiGroup) return;
      counts.set(r.jhiGroup, (counts.get(r.jhiGroup) || 0) + 1);
    });
    return Array.from(counts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [recipients]);

  const recipientCount = mode === 'group'
    ? (groups.find((g) => g.name === selectedGroup)?.count || 0)
    : selectedIndividuals.length;

  const segmentInfo = computeSmsSegments(message);
  const totalParts = recipientCount * segmentInfo.segments;
  const canSend = message.trim().length > 0 && recipientCount > 0;

  const handleModeChange = (_event: React.MouseEvent<HTMLElement>, newMode: 'group' | 'individual' | null) => {
    if (!newMode) return;
    setMode(newMode);
    setError('');
    setSuccess('');
  };

  const handleSend = async () => {
    setConfirmOpen(false);
    setLoading(true);
    setError('');
    setSuccess('');

    if (!cookies?.['UserAuth']?.token) {
      setError('Authentication token not found');
      setLoading(false);
      return;
    }

    try {
      const payload = mode === 'group'
        ? { mailingListName: selectedGroup, message }
        : { recipientIds: selectedIndividuals.map((m) => m.id), message };

      const response = await fetch(`${baseBackendUrl}/api/sms/send-sms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${cookies['UserAuth'].token}`
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error('Failed to send SMS');
      setSuccess(`Text message queued for ${recipientCount} recipient${recipientCount === 1 ? '' : 's'}.`);
      setMessage('');
      setSelectedGroup('');
      setSelectedIndividuals([]);
    } catch (err) {
      setError('Failed to send text message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!IsAdmin) {
    return (
      <Box sx={{ p: 3, pt: 10, textAlign: 'center' }}>
        <Typography variant="h5" color="error">Access Denied</Typography>
        <Typography>You do not have permission to access this page.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, pt: 10, maxWidth: 900, margin: '0 auto' }}>
      <Typography variant="h4" gutterBottom>Text Messages</Typography>
      <Typography variant="subtitle1" color="text.secondary" paragraph>
        Send a text message via AWS SNS to a mailing list group, or to a hand-picked group of people.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {loadingRecipients ? (
        <Box sx={{ textAlign: 'center', p: 4 }}><CircularProgress /></Box>
      ) : (
        <Paper sx={{ p: 3 }}>
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>Who should receive this text?</Typography>
              <ToggleButtonGroup value={mode} exclusive onChange={handleModeChange} size="small">
                <ToggleButton value="group">A group</ToggleButton>
                <ToggleButton value="individual">Hand-picked people</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {mode === 'group' ? (
              <FormControl fullWidth>
                <InputLabel>Mailing List Group</InputLabel>
                <Select
                  value={selectedGroup}
                  label="Mailing List Group"
                  onChange={(e) => setSelectedGroup(e.target.value)}
                >
                  {groups.map((g) => (
                    <MenuItem key={g.name} value={g.name}>
                      {g.name} ({g.count} with a phone number)
                    </MenuItem>
                  ))}
                </Select>
                {groups.length === 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    No groups have members with phone numbers yet. Add phone numbers in List Management first.
                  </Typography>
                )}
              </FormControl>
            ) : (
              <Autocomplete
                multiple
                options={recipients}
                value={selectedIndividuals}
                onChange={(_e, value) => setSelectedIndividuals(value)}
                disableCloseOnSelect
                getOptionLabel={(m) => `${m.fullName || 'Unnamed'} — ${m.phoneNumber}`}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderOption={(props, option, { selected }) => (
                  <li {...props} key={option.id}>
                    <Checkbox checked={selected} sx={{ mr: 1 }} />
                    {option.fullName || 'Unnamed'} — {option.phoneNumber}
                    {option.jhiGroup && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        ({option.jhiGroup})
                      </Typography>
                    )}
                  </li>
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip label={option.fullName || option.phoneNumber} {...getTagProps({ index })} key={option.id} />
                  ))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Search by name" placeholder="Type a name to add someone" />
                )}
              />
            )}

            <Box>
              <TextField
                fullWidth
                multiline
                minRows={4}
                label="Message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {segmentInfo.length} characters ({segmentInfo.encoding}) &middot; {segmentInfo.segments || 0} segment{segmentInfo.segments === 1 ? '' : 's'} per recipient
                {recipientCount > 0 && ` · ${totalParts} message part${totalParts === 1 ? '' : 's'} billed by AWS for ${recipientCount} recipient${recipientCount === 1 ? '' : 's'}`}
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary">
              {recipientCount > 0
                ? `This will send to ${recipientCount} recipient${recipientCount === 1 ? '' : 's'}.`
                : 'Select a group or pick individuals to see how many people will receive this text.'}
            </Typography>

            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                color="primary"
                disabled={!canSend || loading}
                onClick={() => setConfirmOpen(true)}
                sx={{ minWidth: 150 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Send Text Message'}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Send this text message?</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            You're about to send to <strong>{recipientCount}</strong> recipient{recipientCount === 1 ? '' : 's'}
            {' '}({totalParts} message part{totalParts === 1 ? '' : 's'} billed by AWS). This can't be undone.
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover' }}>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{message}</Typography>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleSend} variant="contained" color="primary">Send</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SendSms;
